import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { format as formatTZ, toZonedTime } from "date-fns-tz";
import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InAppChatShell from "@/components/in-app-chat-shell/InAppChatShell";
import { PageHeader } from "@/components/layouts/PageHeader";
import NewsFeedContent from "@/components/news-feed/NewsFeedContent";
import AnalysisContentManagementPanel from "@/components/news-feed/news-feed-control-panels/AnalysisContentManagementPanel";
import AnalysisControlPanel from "@/components/news-feed/news-feed-control-panels/AnalysisControlPanel";
import type { NewsFeedItem } from "@/data-access/news-feeds/queries/news-feeds-queries";
import type { FeedArticleProjectionFilterInput } from "@/data-access/shared/types/feed-article-projection.types";
import useElementHeight from "@/hooks/useElementHeight";
import useMergeSearchNavigate from "@/hooks/useMergeSearchNavigate";
import { resolveFeedSwitchHref } from "@/lib/auth/feedLastLocationUtils";
import { useFeature } from "@/lib/feature-management";
import { cn } from "@/lib/utils";
import {
  calculateDateRangeFromDays,
  daysToRelativeDateRange,
  formatDateForUrl,
  getPeriodForDateRange,
} from "@/lib/utils/dateUtils";
import NewsFeedFilterSidebar from "@/pages/news-feed/NewsFeedFilterSidebar";
import { logoutUser } from "@/store/auth.async-actions";
import useAuth, { type AuthState } from "@/store/auth.slice";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";
import {
  AnalysisParamsProvider,
  AnalysisSidebarFilterParamsProvider,
} from "@/store/news-feed/params/analysis-params.context";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";
import useNewsFeedsStore from "@/store/news-feeds/news-feeds.slice";

const routeApi = getRouteApi("/_authenticated/analysis/$feedId");

/**
 * Computes effective start/end dates from search params.
 * If custom dates provided, use those. Otherwise calculate from filterDays.
 */
const computeDates = (
  filterDays: number,
  startDateParam?: string,
  endDateParam?: string,
): { startDate: string; endDate: string; isCustomDaysRange: boolean } => {
  const timeZone = "UTC";
  if (startDateParam && endDateParam) {
    return {
      startDate: startDateParam,
      endDate: endDateParam,
      isCustomDaysRange: true,
    };
  }
  const { startDate, endDate } = calculateDateRangeFromDays(filterDays);
  return {
    startDate: formatTZ(toZonedTime(startDate, timeZone), "yyyy-MM-dd", {
      timeZone,
    }),
    endDate: formatTZ(toZonedTime(endDate, timeZone), "yyyy-MM-dd", {
      timeZone,
    }),
    isCustomDaysRange: false,
  };
};

/** Map store feeds to NewsFeedItem for PageHeader switcher. */
const toNewsFeedItems = (
  feeds: Array<{ id: string | number; name: string; image?: string | null }>,
): NewsFeedItem[] =>
  feeds.map((f) => ({
    id: String(f.id),
    name: f.name,
    image: f.image ?? null,
  }));

const LARGE_SCREEN_BREAKPOINT = 1024;

interface AnalysisRouteContentProps {
  stickyHeaderRef: RefObject<HTMLDivElement | null>;
  feedItems: NewsFeedItem[];
  userEmail: string;
  userTenantName: string | undefined;
}

const AnalysisRouteContent = ({
  stickyHeaderRef,
  feedItems,
  userEmail,
  userTenantName,
}: AnalysisRouteContentProps) => {
  const analysisContentRef = useRef<HTMLDivElement>(null);
  const enableChatContextAnalysisFab = useFeature(
    "enable-chat-context-analysis",
    false,
  );
  const { feedId } = routeApi.useParams();
  const searchParams = routeApi.useSearch();
  const navigate = useNavigate();
  const setFeedId = useNewsFeedsStore((state) => state.setFeedId);
  const feedLastLocations = useAuth((state) => state.user.feed_last_locations);

  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth >= LARGE_SCREEN_BREAKPOINT
      : true,
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < LARGE_SCREEN_BREAKPOINT) {
        setIsFilterSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { startDate, endDate, isCustomDaysRange } = computeDates(
    searchParams.filterDays,
    searchParams.startDate,
    searchParams.endDate,
  );

  const mergeSearchAndNavigate = useMergeSearchNavigate();

  const onDateRangeApply = useCallback(
    (range: { from?: Date; to?: Date; days?: number; isCustom?: boolean }) => {
      useFeedContentManagementStore.getState().resetFeedVolumeView();
      const period = getPeriodForDateRange(range.from, range.to, range.days);

      if (range.isCustom && range.from && range.to) {
        mergeSearchAndNavigate({
          startDate: formatDateForUrl(range.from),
          endDate: formatDateForUrl(range.to),
          period: period ?? "day",
        });
      } else if (range.days) {
        mergeSearchAndNavigate({
          filterDays: range.days,
          startDate: undefined,
          endDate: undefined,
          period: period ?? "day",
        });
      }
    },
    [mergeSearchAndNavigate],
  );

  const {
    feedId: queryFeedId,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
    selectedFilters,
    chartSelection,
  } = useAnalysisParamsForQueries();

  const analysisChatFilterContext =
    useMemo((): FeedArticleProjectionFilterInput | null => {
      if (
        queryFeedId == null ||
        effectiveStartDate == null ||
        effectiveEndDate == null
      ) {
        return null;
      }
      // Relative selections travel as period; custom ranges as absolute dateRange.
      const relativePeriod = isCustomDaysRange
        ? undefined
        : daysToRelativeDateRange(searchParams.filterDays);
      return {
        feedId: queryFeedId,
        ...(relativePeriod
          ? { period: relativePeriod }
          : {
              dateRange: {
                startDate: effectiveStartDate,
                endDate: effectiveEndDate,
              },
            }),
        ...(chartSelection ? { chartSelection } : {}),
        ...(selectedFilters ?? {}),
      };
    }, [
      queryFeedId,
      effectiveStartDate,
      effectiveEndDate,
      isCustomDaysRange,
      searchParams.filterDays,
      chartSelection,
      selectedFilters,
    ]);

  return (
    <InAppChatShell
      overlayContentRef={analysisContentRef}
      mode="analysis"
      filterContext={analysisChatFilterContext}
      showOpenChatFab={enableChatContextAnalysisFab}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <PageHeader
          label="Analysis"
          showFeedSwitcher
          feeds={feedItems}
          selectedFeedId={feedId}
          onFeedChange={(id) => {
            setFeedId(id);
            navigate({
              href: resolveFeedSwitchHref(id, "ANALYSIS", feedLastLocations),
            });
          }}
          userEmail={userEmail}
          userTenantName={userTenantName}
          onLogout={logoutUser}
        />

        <div
          className={cn(
            "grid min-h-0 flex-1 grid-cols-1",
            isFilterSidebarOpen && "sm:grid-cols-[auto_1fr]",
          )}
        >
          {isFilterSidebarOpen && (
            <AnalysisSidebarFilterParamsProvider>
              <NewsFeedFilterSidebar
                onCollapse={() => setIsFilterSidebarOpen(false)}
              />
            </AnalysisSidebarFilterParamsProvider>
          )}
          <div
            ref={analysisContentRef}
            className="w-full max-w-full min-w-0 shrink-0 overflow-x-auto overflow-y-auto grow"
          >
            <div
              ref={stickyHeaderRef}
              className="sticky top-0 z-10 flex w-[calc(100%-var(--in-app-chat-overlay-width,0px))] flex-col gap-4 bg-white pb-4"
            >
              <AnalysisContentManagementPanel
                feedId={feedId}
                filterDays={searchParams.filterDays}
                isCustomDaysRange={isCustomDaysRange}
                startDate={startDate}
                endDate={endDate}
                onDateRangeApply={onDateRangeApply}
                isFilterSidebarOpen={isFilterSidebarOpen}
                onToggleFilterSidebar={() =>
                  setIsFilterSidebarOpen((prev) => !prev)
                }
              />
              <div className="mx-auto w-full max-w-[964px] px-4">
                <AnalysisControlPanel />
              </div>
            </div>
            <NewsFeedContent />
          </div>
        </div>
      </div>
    </InAppChatShell>
  );
};

/**
 * Analysis route (TanStack Router).
 * Uses NewsFeedFilterSidebar with Analysis params context; route owns sidebar open state.
 */
const AnalysisRoute = () => {
  const { feedId } = routeApi.useParams();
  const searchParams = routeApi.useSearch();
  const navigateWithSearch = useMergeSearchNavigate();
  const feeds = useNewsFeedsStore((state) => state.allNewsFeeds);
  const setFeedId = useNewsFeedsStore((state) => state.setFeedId);
  const feedItems = toNewsFeedItems(feeds);
  const user = useAuth((state: AuthState) => state.user);
  const updateSelectedKeyMessageFilterExclusion = useKeyMessageStore(
    (state) => state.updateSelectedKeyMessageFilterExclusion,
  );

  useEffect(() => {
    setFeedId(feedId);
    updateSelectedKeyMessageFilterExclusion("OR");
  }, [feedId, setFeedId, updateSelectedKeyMessageFilterExclusion]);

  const { startDate, endDate, isCustomDaysRange } = computeDates(
    searchParams.filterDays,
    searchParams.startDate,
    searchParams.endDate,
  );

  const { ref: stickyHeaderRef, height: stickyHeaderHeight } =
    useElementHeight();

  const analysisParamsValue = useMemo(
    () => ({
      feedId,
      startDate,
      endDate,
      filterDays: searchParams.filterDays,
      period: searchParams.period,
      isCustomDaysRange,
      selectedFilters: searchParams.selected,
      keyMessageConjunction: searchParams.keyMessageConjunction,
      stickyHeaderHeight,
      navigateWithSearch,
    }),
    [
      feedId,
      startDate,
      endDate,
      searchParams.filterDays,
      searchParams.period,
      isCustomDaysRange,
      searchParams.selected,
      searchParams.keyMessageConjunction,
      stickyHeaderHeight,
      navigateWithSearch,
    ],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <AnalysisParamsProvider value={analysisParamsValue}>
        <AnalysisRouteContent
          stickyHeaderRef={stickyHeaderRef}
          feedItems={feedItems}
          userEmail={user?.email ?? ""}
          userTenantName={user?.default_tenant?.name}
        />
      </AnalysisParamsProvider>
    </div>
  );
};

export default AnalysisRoute;
